import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  useTheme,
  Tabs,
  Tab,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ============================================================
// HELPERS (same shape as Attendance.js / FeeDeposit.js)
// ============================================================

const getImageUrl = (image) => {
  if (!image) return '';

  if (image.startsWith('http://') || image.startsWith('https://')) {
    return image;
  }

  return `${API_BASE_URL}${image.startsWith('/') ? '' : '/'}${image}`;
};

const getAuthToken = () => {
  if (typeof window === 'undefined') return null;

  return (
    localStorage.getItem('accessToken') ||
    localStorage.getItem('token') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('access_token') ||
    sessionStorage.getItem('accessToken') ||
    sessionStorage.getItem('token')
  );
};

const getSessionOrganizationId = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user')) || {};
    return user?.organization_id || null;
  } catch {
    return null;
  }
};

// Same status list / colors as Attendance.js
const STATUS_OPTIONS = [
  { value: 'present', label: 'Present', color: 'success' },
  { value: 'absent', label: 'Absent', color: 'error' },
  { value: 'half_day', label: 'Half Day', color: 'warning' },
  { value: 'on_leave', label: 'On Leave', color: 'info' },
];

const getStatusMeta = (status) => {
  const match = STATUS_OPTIONS.find((s) => s.value === status);

  if (match) {
    return {
      label: match.label,
      color: match.color,
      icon:
        status === 'present' ? (
          <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
        ) : status === 'absent' ? (
          <HighlightOffIcon sx={{ fontSize: 14 }} />
        ) : undefined,
    };
  }

  return {
    label: status || 'Unknown',
    color: 'default',
    icon: undefined,
  };
};

const fmtDate = (value) => {
  if (!value) return '—';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const ViewStudentList = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { studentId } = useParams();

  // ── Student profile ────────────────────────────────────────
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [value, setValue] = useState(0);

  // ── Attendance tab ──────────────────────────────────────────
  const [attendanceFetched, setAttendanceFetched] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceError, setAttendanceError] = useState('');
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState(null);

  // ── Fees tab ────────────────────────────────────────────────
  const [feeFetched, setFeeFetched] = useState(false);
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeError, setFeeError] = useState('');
  const [feeRecords, setFeeRecords] = useState([]);

  const [expandedFeeId, setExpandedFeeId] = useState(null);
  const [installmentDataMap, setInstallmentDataMap] = useState({});
  const [installmentLoadingId, setInstallmentLoadingId] = useState(null);
  const [installmentError, setInstallmentError] = useState('');

  // ============================================================
  // FETCH ADMISSION DETAILS (prefill API — same endpoint
  // EditAdmission.js uses: GET /api/admission/admissions/{id}/)
  // ============================================================
  useEffect(() => {
    const fetchStudentDetails = async () => {
      setLoading(true);
      setErrorMsg('');

      try {
        const accessToken = getAuthToken();

        const response = await axios.get(
          `${API_BASE_URL}/api/admission/admissions/${studentId}/`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        setStudentData(response.data);
      } catch (error) {
        console.error('Failed to fetch student details:', error);
        setErrorMsg('Failed to load student details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (studentId) {
      fetchStudentDetails();
    }
  }, [studentId]);

  // ============================================================
  // ATTENDANCE — same endpoint Attendance.js calls for a single
  // student: GET /api/admission/student-attendance/{id}/
  // ============================================================
  const fetchAttendance = async () => {
    setAttendanceLoading(true);
    setAttendanceError('');

    try {
      const accessToken = getAuthToken();

      const response = await axios.get(
        `${API_BASE_URL}/api/admission/student-attendance/${studentId}/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = response.data || {};

      const records = Array.isArray(data.history) ? data.history : [];

      const sorted = [...records].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );

      setAttendanceHistory(sorted);

      const presentDays =
        data.summary?.present_days ??
        sorted.filter((r) => r.status === 'present').length;

      const absentDays =
        data.summary?.absent_days ??
        sorted.filter((r) => r.status === 'absent').length;

      const halfDays =
        data.summary?.half_days ??
        sorted.filter((r) => r.status === 'half_day').length;

      const leaveDays =
        data.summary?.leave_days ??
        sorted.filter((r) => r.status === 'on_leave').length;

      const workingDays = data.summary?.working_days ?? sorted.length;

      const attendancePercentage =
        data.summary?.attendance_percentage ??
        (workingDays > 0 ? Math.round((presentDays / workingDays) * 100) : 0);

      setAttendanceSummary({
        workingDays,
        presentDays,
        absentDays,
        halfDays,
        leaveDays,
        attendancePercentage,
      });
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
      setAttendanceError('Failed to load attendance details.');
    } finally {
      setAttendanceLoading(false);
    }
  };

  // ============================================================
  // FEES — same endpoint FeeDeposit.js / FeeGeneration.js use:
  // GET /api/fee_details/fee-generation/list/?organization=&branch=
  // then keep only the rows that belong to this student.
  // ============================================================
  const fetchFeeRecords = async () => {
    setFeeLoading(true);
    setFeeError('');

    try {
      const accessToken = getAuthToken();

      const params = new URLSearchParams();

      const orgId = studentData?.organization || getSessionOrganizationId();
      const branchId = studentData?.branch;

      if (orgId) {
        params.append('organization', orgId);
      }

      if (branchId) {
        params.append('branch', branchId);
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/fee_details/fee-generation/list/?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const list = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];

      const admissionCode = String(studentData?.admission_code || '').trim();

      const matched = list.filter((item) => {
        const itemAdmission = String(
          item.admission_no || item.admission_code || ''
        ).trim();

        return (
          (admissionCode && itemAdmission === admissionCode) ||
          String(item.admission) === String(studentId)
        );
      });

      setFeeRecords(matched);
    } catch (error) {
      console.error('Failed to fetch fee records:', error);
      setFeeError('Failed to load fee details.');
    } finally {
      setFeeLoading(false);
    }
  };

  // ============================================================
  // INSTALLMENTS FOR ONE FEE RECORD — same endpoint
  // FeeDeposit.js uses:
  // GET /api/fee_details/fee-generation/{fee_id}/installments-with-deposits/
  // ============================================================
  const handleToggleInstallments = async (feeId) => {
    if (expandedFeeId === feeId) {
      setExpandedFeeId(null);
      return;
    }

    setExpandedFeeId(feeId);

    if (installmentDataMap[feeId]) {
      return;
    }

    setInstallmentLoadingId(feeId);
    setInstallmentError('');

    try {
      const accessToken = getAuthToken();

      const response = await axios.get(
        `${API_BASE_URL}/api/fee_details/fee-generation/${feeId}/installments-with-deposits/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      setInstallmentDataMap((prev) => ({
        ...prev,
        [feeId]: response.data,
      }));
    } catch (error) {
      console.error('Failed to fetch installment details:', error);
      setInstallmentError('Failed to load installment schedule.');
    } finally {
      setInstallmentLoadingId(null);
    }
  };

  // ============================================================
  // TAB CHANGE — lazy-load each tab's data the first time it's opened
  // ============================================================
  const handleChange = (event, newValue) => {
    setValue(newValue);

    if (newValue === 0 && !attendanceFetched) {
      setAttendanceFetched(true);
      fetchAttendance();
    }

    if (newValue === 1 && !feeFetched) {
      setFeeFetched(true);
      fetchFeeRecords();
    }
  };

  // Load the default (first) tab automatically once the student is known
  useEffect(() => {
    if (studentData && !attendanceFetched) {
      setAttendanceFetched(true);
      fetchAttendance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentData]);

  // ============================================================
  // HELPERS — course / batch text
  // ============================================================
  const getCourseNames = () => {
    if (!studentData?.courses || studentData.courses.length === 0) {
      return '—';
    }

    return studentData.courses
      .map((c) => c.course_name || c.course || `Course ${c.course_id}`)
      .join(', ');
  };

  const getBatchNames = () => {
    if (!studentData?.courses || studentData.courses.length === 0) {
      return '—';
    }

    const allBatches = [];

    studentData.courses.forEach((course) => {
      if (course.batches && course.batches.length > 0) {
        course.batches.forEach((batch) => {
          allBatches.push(
            batch.batch_name || batch.name || `Batch ${batch.batch_id}`
          );
        });
      }
    });

    return allBatches.length > 0 ? allBatches.join(', ') : '—';
  };

  // ============================================================
  // LOADING / ERROR STATES FOR THE PROFILE ITSELF
  // ============================================================
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3, mb: 4, textAlign: 'center' }}>
        <CircularProgress sx={{ mt: 10 }} />
        <Typography sx={{ mt: 2, color: theme.palette.text.primary }}>
          Loading student details...
        </Typography>
      </Container>
    );
  }

  if (errorMsg || !studentData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3, mb: 4, textAlign: 'center' }}>
        <Typography color="error" sx={{ mt: 5, mb: 3 }}>
          {errorMsg || 'Student not found.'}
        </Typography>
        <Button variant="contained" onClick={() => navigate(-1)}>
          BACK
        </Button>
      </Container>
    );
  }

  // ============================================================
  // MAIN VIEW
  // ============================================================
  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {/* PHOTO / AVATAR */}
        <Box sx={{ textAlign: 'center' }}>
          {studentData.image ? (
            <Avatar
              src={getImageUrl(studentData.image)}
              sx={{ width: 120, height: 120, mb: 2 }}
            />
          ) : (
            <Avatar
              sx={{
                width: 120,
                height: 120,
                bgcolor: theme.palette.warning.main,
                mb: 2,
                fontSize: '48px',
              }}
            >
              {studentData.candidate_name?.charAt(0)?.toUpperCase() || '?'}
            </Avatar>
          )}

          <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
            {studentData.candidate_name || '—'}
          </Typography>
        </Box>

        {/* DETAILS */}
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              mb: 3,
            }}
          >
            {[
              { label: 'Admission No.', value: studentData.admission_code },
              { label: "Father's Name", value: studentData.father_name },
              { label: "Mother's Name", value: studentData.mother_name },
              { label: 'Qualification', value: studentData.qualification },
              { label: 'Address', value: studentData.address },
              { label: 'Date of Admission', value: studentData.admission_date },
              { label: 'Mobile No.', value: studentData.mobile_no },
              {
                label: 'Alternate Mobile No.',
                value: studentData.alternate_mobile_no,
              },
              { label: 'Email Id', value: studentData.email },
              { label: 'Gender', value: studentData.gender },
              { label: 'Aadhaar No.', value: studentData.aadhaar_no },
              { label: 'Course(s)', value: getCourseNames() },
              { label: 'Batch(es)', value: getBatchNames() },
              { label: 'Status', value: studentData.status },
            ].map((field, index) => (
              <TextField
                key={index}
                label={field.label}
                variant="outlined"
                fullWidth
                value={field.value || '—'}
                InputProps={{
                  readOnly: true,
                  sx: {
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
                InputLabelProps={{
                  sx: { color: theme.palette.text.primary },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: theme.palette.divider },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
              />
            ))}
          </Box>

          {/* ADDITIONAL INFO TABS */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: theme.palette.divider,
              borderRadius: 1,
              p: 2,
              mb: 2,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 1, color: theme.palette.text.primary }}
            >
              Additional Information
            </Typography>

            <Tabs
              value={value}
              onChange={handleChange}
              sx={{
                mb: 2,
                '& .MuiTab-root': { color: theme.palette.text.primary },
                '& .Mui-selected': { color: theme.palette.primary.main },
                '& .MuiTabs-indicator': {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            >
              <Tab label="Attendance Details" />
              <Tab label="Fees Details" />
            </Tabs>

            {/* ==================================================
                ATTENDANCE PANEL
            ================================================== */}
            {value === 0 && (
              <Box>
                {attendanceLoading ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={26} />
                  </Box>
                ) : attendanceError ? (
                  <Typography color="error" sx={{ py: 2 }}>
                    {attendanceError}
                  </Typography>
                ) : (
                  <>
                    {attendanceSummary && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                          mb: 2,
                          p: 1.5,
                          borderRadius: 1.5,
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255,255,255,0.05)'
                              : 'rgba(0,0,0,0.03)',
                        }}
                      >
                        {[
                          [
                            'Working Days',
                            attendanceSummary.workingDays,
                            'text.primary',
                          ],
                          [
                            'Present',
                            attendanceSummary.presentDays,
                            'success.main',
                          ],
                          ['Absent', attendanceSummary.absentDays, 'error.main'],
                          ...(attendanceSummary.halfDays > 0
                            ? [
                                [
                                  'Half Day',
                                  attendanceSummary.halfDays,
                                  'warning.main',
                                ],
                              ]
                            : []),
                          ...(attendanceSummary.leaveDays > 0
                            ? [
                                [
                                  'On Leave',
                                  attendanceSummary.leaveDays,
                                  'info.main',
                                ],
                              ]
                            : []),
                          [
                            'Attendance Rate',
                            `${attendanceSummary.attendancePercentage}%`,
                            'primary.main',
                          ],
                        ].map(([label, val, color]) => (
                          <Box key={label} sx={{ textAlign: 'center', minWidth: 90 }}>
                            <Typography
                              variant="h6"
                              sx={{ color, fontWeight: 700, lineHeight: 1.2 }}
                            >
                              {val}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {attendanceHistory.length === 0 ? (
                      <Typography color="text.disabled" sx={{ py: 2 }}>
                        No attendance records found for this student.
                      </Typography>
                    ) : (
                      <TableContainer component={Paper} elevation={0}>
                        <Table size="small">
                          <TableHead>
                            <TableRow
                              sx={{ backgroundColor: theme.palette.primary.main }}
                            >
                              <TableCell
                                sx={{ color: '#fff', fontWeight: 700 }}
                              >
                                S.No
                              </TableCell>
                              {[
                                'Date',
                                'Day',
                                'Status',
                                'Time In',
                                'Time Out',
                                'Total Hours',
                                'Remark',
                              ].map((h) => (
                                <TableCell
                                  key={h}
                                  sx={{ color: '#fff', fontWeight: 700 }}
                                >
                                  {h}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {attendanceHistory.map((record, idx) => {
                              const recordDate = new Date(record.date);

                              const dayName = Number.isNaN(recordDate.getTime())
                                ? '—'
                                : recordDate.toLocaleDateString('en-US', {
                                    weekday: 'long',
                                  });

                              const statusMeta = getStatusMeta(record.status);

                              return (
                                <TableRow key={record.id ?? idx}>
                                  <TableCell>{idx + 1}</TableCell>
                                  <TableCell>{fmtDate(record.date)}</TableCell>
                                  <TableCell>{dayName}</TableCell>
                                  <TableCell>
                                    <Chip
                                      icon={statusMeta.icon}
                                      label={statusMeta.label}
                                      size="small"
                                      color={statusMeta.color}
                                    />
                                  </TableCell>
                                  <TableCell>{record.time_in || '—'}</TableCell>
                                  <TableCell>{record.time_out || '—'}</TableCell>
                                  <TableCell>
                                    {record.total_hours ?? '—'}
                                  </TableCell>
                                  <TableCell>{record.remark || '—'}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                )}
              </Box>
            )}

            {/* ==================================================
                FEES PANEL
            ================================================== */}
            {value === 1 && (
              <Box>
                {feeLoading ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={26} />
                  </Box>
                ) : feeError ? (
                  <Typography color="error" sx={{ py: 2 }}>
                    {feeError}
                  </Typography>
                ) : feeRecords.length === 0 ? (
                  <Typography color="text.disabled" sx={{ py: 2 }}>
                    No fee records found for this student.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {feeRecords.map((record) => {
                      const isExpanded = expandedFeeId === record.fee_id;
                      const detail = installmentDataMap[record.fee_id];
                      const isLoadingThis =
                        installmentLoadingId === record.fee_id;

                      const totalAmount = Number(
                        record.amount ?? record.total_fee ?? 0
                      );
                      const totalPaid = Number(record.total_paid ?? 0);
                      const balanceAmount = Number(
                        record.balance_amount ?? record.pending_amount ?? 0
                      );

                      return (
                        <Paper
                          key={record.fee_id}
                          elevation={0}
                          sx={{
                            border: '1px solid',
                            borderColor: theme.palette.divider,
                            borderRadius: 2,
                            p: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Chip
                                label={record.fee_type || '—'}
                                size="small"
                                color={
                                  record.fee_type === 'Monthly'
                                    ? 'secondary'
                                    : 'info'
                                }
                              />

                              <Typography variant="caption" color="text.disabled">
                                Generated: {fmtDate(record.generated_date)}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: 'flex',
                                gap: 2.5,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700 }}
                                >
                                  ₹
                                  {totalAmount.toLocaleString('en-IN', {
                                    minimumFractionDigits: 2,
                                  })}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                >
                                  Total
                                </Typography>
                              </Box>

                              <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700, color: 'success.main' }}
                                >
                                  ₹
                                  {totalPaid.toLocaleString('en-IN', {
                                    minimumFractionDigits: 2,
                                  })}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                >
                                  Paid
                                </Typography>
                              </Box>

                              <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color:
                                      balanceAmount > 0
                                        ? 'error.main'
                                        : 'success.main',
                                  }}
                                >
                                  ₹
                                  {balanceAmount.toLocaleString('en-IN', {
                                    minimumFractionDigits: 2,
                                  })}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                >
                                  Balance
                                </Typography>
                              </Box>
                            </Box>

                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                handleToggleInstallments(record.fee_id)
                              }
                            >
                              {isExpanded ? 'Hide' : 'View'} Installments
                            </Button>
                          </Box>

                          {isExpanded && (
                            <Box sx={{ mt: 2 }}>
                              {isLoadingThis ? (
                                <Box sx={{ textAlign: 'center', py: 2 }}>
                                  <CircularProgress size={22} />
                                </Box>
                              ) : installmentError ? (
                                <Typography color="error" variant="body2">
                                  {installmentError}
                                </Typography>
                              ) : detail?.installments?.length > 0 ? (
                                <TableContainer>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        {[
                                          'Inst. #',
                                          'Due Date',
                                          'Amount',
                                          'Paid',
                                          'Remaining',
                                          'Status',
                                        ].map((h) => (
                                          <TableCell
                                            key={h}
                                            sx={{ fontWeight: 700 }}
                                          >
                                            {h}
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    </TableHead>

                                    <TableBody>
                                      {detail.installments.map((inst) => (
                                        <TableRow key={inst.installment_id}>
                                          <TableCell>
                                            #{inst.installment_no}
                                          </TableCell>
                                          <TableCell>
                                            {fmtDate(inst.due_date)}
                                          </TableCell>
                                          <TableCell>
                                            ₹
                                            {Number(
                                              inst.amount || 0
                                            ).toLocaleString('en-IN', {
                                              minimumFractionDigits: 2,
                                            })}
                                          </TableCell>
                                          <TableCell>
                                            ₹
                                            {Number(
                                              inst.total_paid || 0
                                            ).toLocaleString('en-IN', {
                                              minimumFractionDigits: 2,
                                            })}
                                          </TableCell>
                                          <TableCell>
                                            ₹
                                            {Number(
                                              inst.remaining_amount || 0
                                            ).toLocaleString('en-IN', {
                                              minimumFractionDigits: 2,
                                            })}
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              label={
                                                inst.is_paid
                                                  ? 'Paid'
                                                  : 'Pending'
                                              }
                                              size="small"
                                              color={
                                                inst.is_paid
                                                  ? 'success'
                                                  : 'error'
                                              }
                                            />
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.disabled"
                                >
                                  No installment schedule found.
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Button
          variant="contained"
          onClick={() => navigate(-1)}
          sx={{
            backgroundColor: theme.palette.primary.main,
            '&:hover': { backgroundColor: theme.palette.primary.dark },
          }}
        >
          BACK
        </Button>
      </Box>
    </Container>
  );
};

export default ViewStudentList;